import { Group, Text, Tooltip } from '@mantine/core'
import type { AssetAssignment } from '@opsopti/shared'
import {
  formatDate,
  useAssetAssignmentCreateMutation,
  useAssetAssignmentsQuery,
  useAssetAssignmentUpdateMutation,
  useGlobalState,
} from '@opsopti/shared'
import type { PayloadFindRequestParams } from '@opsopti/shared/src/services/api/api.types'
import {
  getHasManyRelationshipIds,
  getHasManyRelationships,
  getHasOneRelationship,
  getHasOneRelationshipId,
} from '@opsopti/shared/src/utils/payloadHelpers'
import {
  IconArrowBack,
  IconArrowUpRight,
  IconMapPin,
  IconPackage,
  IconUsers,
  IconViewfinder,
} from '@tabler/icons-react'
import type { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { createMRTColumnHelper } from 'mantine-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'

import type { TableProps } from '@/components/Table'
import {
  CellTextWithTooltip,
  EditTextarea,
  HeaderCellWithTooltip,
  Table,
  TableRowActionButton,
} from '@/components/Table'
import { theme } from '@/configs/theme'
import { AssetSentryAssignmentFormScreen } from '@/screens/AssetSentryAssignmentFormScreen'
import { tableScreenIcon } from '@/screens/TableScreen'
import {
  castPaginationStateToQueryParams,
  getFormScreenTitle,
  showErrorNotification,
  showSuccessNotification,
} from '@/utils/componentCommons'

import { AssetsTable } from './AssetsTable'
import { AssetTargetsTable } from './AssetTargetsTable'
import { UsersTable } from './UsersTable'

enum ColIds {
  CompDirection = 'computed-direction',
  Direction = 'direction',
  CreatedAt = 'created-at',
  Asset = 'asset',
  Target = 'target',
  Assignees = 'assignees',
  Creator = 'creator',
  SupervisorNotes = 'supervisor-notes',
  MemberNotes = 'member-notes',
}

type AssetAssignmentsTableProps = {
  where?: PayloadFindRequestParams['where']
  excludeColumns?: `${ColIds}`[]
  TableProps?: Omit<TableProps<AssetAssignment>, 'data' | 'columns'>
}

export const AssetAssignmentsTable = observer(function AssetAssignmentsTable(props: AssetAssignmentsTableProps) {
  const { authenticationStore, uiStore } = useGlobalState()

  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 })

  const assignmentsQuery = useAssetAssignmentsQuery({
    params: {
      depth: '2',
      where: props.where,
      select: {
        createdAt: true,
        asset: true,
        assetTarget: true,
        assetTracker: true,
        assetTargetAsset: true,
        assignees: true,
        internalNotes: true,
        memberNotes: true,
        creator: true,
        current: true,
      },
      ...castPaginationStateToQueryParams(pagination),
    },
  })

  const assignmentCreateMutation = useAssetAssignmentCreateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  function assignToDefault(data: AssetAssignment) {
    const asset = getHasOneRelationship(data.asset)
    const assetGroup = getHasOneRelationship(asset?.assetGroup)
    const defaultTargetId = getHasOneRelationshipId(assetGroup?.defaultTarget)

    if (!asset || !assetGroup || !defaultTargetId) {
      showErrorNotification({
        title: 'Something went wrong.',
        message: 'Could not assign Asset to Base Target.',
      })
      return
    }

    assignmentCreateMutation.mutateAsync({
      creator: authenticationStore.user?.id!,
      assignees: [authenticationStore.user?.id!],
      asset: asset.id,
      assetTarget: defaultTargetId,
    })
  }

  const columns = useMemo(
    () => assignmentsCols.filter(col => !props.excludeColumns?.includes(col.id as `${ColIds}`)),
    [props.excludeColumns],
  )

  const shouldPaginate = !!assignmentsQuery.data?.totalPages && assignmentsQuery.data.totalPages > 1

  return (
    <Table
      enableGlobalFilter={false}
      enableBottomToolbar={shouldPaginate}
      enableSorting={false}
      columns={columns}
      data={assignmentsQuery.data?.docs ?? []}
      entity={{ singular: 'Assignment', plural: 'Assignments', Icon: IconMapPin }}
      displayColumnDefOptions={{ 'mrt-row-actions': { size: 98 } }}
      enableTableHead={!!assignmentsQuery.data?.docs?.length}
      // pagination
      enablePagination={shouldPaginate}
      state={{
        pagination,
        isLoading: assignmentsQuery.isLoading,
        columnVisibility: {
          [ColIds.CompDirection]: false,
        },
      }}
      onPaginationChange={setPagination}
      rowCount={assignmentsQuery.data?.totalDocs}
      // editing
      enableEditing
      editDisplayMode="cell"
      // row actions
      rowActionsInclude={row => {
        const asset = getHasOneRelationship(row.original.asset)
        const assetGroup = getHasOneRelationship(asset?.assetGroup)
        const defaultTargetId = getHasOneRelationshipId(assetGroup?.defaultTarget)
        const currentTargetId = getHasOneRelationshipId(row.original.assetTarget)

        if (row.original.current === true) {
          if (defaultTargetId === null || currentTargetId === defaultTargetId) {
            return ['assign']
          } else {
            return ['assignToDefault', 'assign']
          }
        }

        return []
      }}
      rowActionsTooltipLabels={{ assignToDefault: 'Assign Asset to Base Target', assign: 'Re-assign Asset' }}
      onRowActionClick={(action, row) => {
        if (action === 'assignToDefault') {
          assignToDefault(row.original)
        } else if (action === 'assign') {
          uiStore.openFormDrawer({
            Component: AssetSentryAssignmentFormScreen,
            ComponentProps: {
              additionalParams: {
                prevAssignmentId: row.original.id,
                assetId: getHasOneRelationshipId(row.original.asset),
              },
              goBackFn: uiStore.closeFormDrawer,
              type: 'drawer',
            },
            TitleProps: {
              Icon: tableScreenIcon['assignments'],
              title: getFormScreenTitle(null, 'Assignment'),
            },
          })
        }
      }}
      // toolbar
      toolbarActionsInclude={['export', 'heightToggle']}
      toolbarActionsState={{ heightToggle: uiStore.tableHeightState['assignments'] }}
      onToolbarActionClick={action => {
        if (action === 'heightToggle') {
          uiStore.toggleTableHeight('assignments')
        }
      }}
      {...props.TableProps}
    />
  )
})

const columnHelper = createMRTColumnHelper<AssetAssignment>()

const assignmentsCols: MRT_ColumnDef<AssetAssignment>[] = [
  columnHelper.accessor(
    row => {
      if (ColIds.CompDirection in row) {
        // For some reason, when there is no data, "row" is actually the columnd ids.
        return
      }

      if (!!row.assetTargetAsset) {
        return 'checked-out'
      }
      const targetId = getHasOneRelationshipId(row.assetTarget)
      const asset = getHasOneRelationship(row.asset)!
      const assetGroup = getHasOneRelationship(asset.assetGroup)!
      const defaultTargetId = getHasOneRelationshipId(assetGroup.defaultTarget)
      if (targetId === defaultTargetId) {
        return 'returned'
      }
      return 'checked-out'
    },
    { id: ColIds.CompDirection, header: '' },
  ),
  columnHelper.display({
    id: ColIds.Direction,
    header: '',
    grow: false,
    size: 60,
    enableEditing: false,
    mantineTableBodyCellProps: props => {
      const direction = props.row.getValue<'checked-out' | 'returned'>(ColIds.CompDirection)
      const noConfirmation = props.row.original.assetTracker === null

      if (direction === 'returned' && noConfirmation) {
        return { style: { backgroundColor: theme.colors.red[1] } }
      }

      return {}
    },
    Cell: props => {
      const direction = props.row.getValue<'checked-out' | 'returned'>(ColIds.CompDirection)
      const noConfirmation = props.row.original.assetTracker === null

      if (direction === 'returned') {
        return (
          <Tooltip
            withArrow
            arrowSize={12}
            position="top"
            offset={8}
            color={theme.colors.red[6]}
            openDelay={250}
            label={'Asset was returned without confirmation.'}
            disabled={!noConfirmation}
            keepMounted={false}>
            <IconArrowBack size={28} color={theme.colors.primaryColors[6]} />
          </Tooltip>
        )
      } else {
        return <IconArrowUpRight size={28} color={theme.colors.cornflower[4]} />
      }
    },
  }),
  columnHelper.accessor(row => new Date(row.createdAt).getTime(), {
    id: ColIds.CreatedAt,
    header: 'Assigned On',
    grow: false,
    size: 190,
    enableEditing: false,
    filterVariant: 'date',
    filterFn: (row, _cId, value) => {
      if (!value) return true

      const rowDate = new Date(row.original.createdAt)
      rowDate.setHours(0, 0, 0, 0)

      return rowDate.getTime() === value.getTime()
    },
    Cell: props => <Text size="sm">{formatDate(props.cell.getValue())}</Text>,
  }),
  columnHelper.accessor(
    row => {
      const user = getHasOneRelationship(row.creator)
      return user?.fullName
    },
    {
      id: ColIds.Creator,
      header: 'Assigned By',
      enableEditing: false,
    },
  ),
  columnHelper.accessor(row => getHasOneRelationship(row.asset)?.nameWithIdentifier, {
    id: ColIds.Asset,
    header: 'Asset',
    grow: true,
    enableEditing: false,
    Cell: props => {
      const assetId = getHasOneRelationshipId(props.row.original.asset)
      const v = props.cell.getValue()

      const { uiStore } = useGlobalState()

      return (
        <Group align="center" gap="xs" wrap="nowrap">
          {!!assetId && (
            <TableRowActionButton
              Icon={IconPackage}
              action="view"
              tooltipLabel="View Asset"
              color="port"
              onClick={() =>
                uiStore.openTableModal({
                  Component: AssetsTable,
                  ComponentProps: {
                    where: { id: { equals: assetId } },
                    TableProps: { enableTopToolbar: false },
                    // excludeColumns: ['assignments', 'assigned'],
                  },
                  TitleProps: {
                    title: `Asset assigned by “${props.row.getValue(ColIds.Creator)}” to “${props.row.getValue(ColIds.Target)}”`,
                    Icon: tableScreenIcon['assets'],
                  },
                })
              }
            />
          )}

          <Text size="sm">{v}</Text>
        </Group>
      )
    },
  }),
  columnHelper.accessor(
    row => {
      const assetTarget = getHasOneRelationship(row.assetTarget)

      if (assetTarget) {
        return assetTarget?.name
      }

      const assetTargetAsset = getHasOneRelationship(row.assetTargetAsset)
      return assetTargetAsset?.nameWithIdentifier
    },
    {
      id: ColIds.Target,
      header: 'Assigned To',
      grow: true,
      enableEditing: false,
      Cell: props => {
        const v = props.cell.getValue()
        const assetTargetId = getHasOneRelationshipId(props.row.original.assetTarget)
        const assetTargetAssetId = getHasOneRelationshipId(props.row.original.assetTargetAsset)

        const { uiStore } = useGlobalState()

        return (
          <Group align="center" gap="xs" wrap="nowrap">
            <TableRowActionButton
              Icon={assetTargetId ? IconViewfinder : IconPackage}
              action="view"
              tooltipLabel="View Target"
              color="port"
              onClick={() =>
                uiStore.openTableModal({
                  Component: assetTargetId ? AssetTargetsTable : AssetsTable,
                  ComponentProps: {
                    where: { id: { equals: assetTargetId ?? assetTargetAssetId } },
                    TableProps: { enableTopToolbar: false },
                  },
                  TitleProps: {
                    title: `Target for “${getHasOneRelationship(props.row.original.asset)?.nameWithIdentifier}” assigned by “${props.row.getValue(ColIds.Creator)}”`,
                    Icon: assetTargetId ? tableScreenIcon['targets'] : tableScreenIcon['assets'],
                  },
                })
              }
            />

            <Text size="sm">{v}</Text>
          </Group>
        )
      },
    },
  ),
  columnHelper.accessor(
    row => {
      const users = getHasManyRelationships(row.assignees)
      return users.map(user => user.fullName).join(', ')
    },
    {
      id: ColIds.Assignees,
      header: 'Assignees',
      grow: true,
      enableEditing: false,
      Cell: props => {
        const v = props.cell.getValue()

        const creatorId = getHasOneRelationshipId(props.row.original.creator)
        const userIds = getHasManyRelationshipIds(props.row.original.assignees)

        const { uiStore } = useGlobalState()

        return (
          <Group align="center" gap="xs" wrap="nowrap">
            <TableRowActionButton
              Icon={IconUsers}
              action="view"
              tooltipLabel="View Assignees"
              color="port"
              onClick={() =>
                uiStore.openTableModal({
                  Component: UsersTable,
                  ComponentProps: {
                    where: { id: { in: [creatorId, ...userIds] } },
                    TableProps: { enableTopToolbar: false },
                  },
                  TitleProps: {
                    title: `Assignes for “${getHasOneRelationship(props.row.original.asset)?.nameWithIdentifier}”`,
                    Icon: tableScreenIcon['users'],
                  },
                })
              }
            />

            <Text size="sm">{v}</Text>
          </Group>
        )
      },
    },
  ),
  columnHelper.accessor('internalNotes', {
    id: ColIds.SupervisorNotes,
    header: 'Notes By Supervisors',
    grow: true,
    minSize: 208,
    maxSize: 250,
    enableSorting: false,
    filterVariant: 'checkbox',
    mantineFilterCheckboxProps: { label: 'Has Notes?' },
    filterFn: (row, _cId, value) => {
      if (value === 'true') {
        return !!row.original.internalNotes
      }

      if (value === 'false') {
        return !row.original.internalNotes
      }

      return true
    },
    Cell: ({ cell }) => <CellTextWithTooltip cell={cell} />,
    Header: ({ column }) => (
      <HeaderCellWithTooltip
        column={column}
        tooltip="Notes that can be used for internal purposes. Only visible to Supervisors."
      />
    ),
    Edit: props => {
      const mutation = useAssetAssignmentUpdateMutation({
        onErrorNotification: showErrorNotification,
        onSuccessNotification: showSuccessNotification,
      })

      return (
        <EditTextarea
          {...props}
          updateFn={v => mutation.mutateAsync({ id: props.row.original.id, internalNotes: v })}
        />
      )
    },
  }),
  columnHelper.accessor('memberNotes', {
    id: ColIds.MemberNotes,
    header: 'Notes By Members',
    grow: true,
    maxSize: 250,
    minSize: 190,
    enableSorting: false,
    filterVariant: 'checkbox',
    mantineFilterCheckboxProps: { label: 'Has Notes?' },
    filterFn: (row, _cId, value) => {
      if (value === 'true') {
        return !!row.original.memberNotes
      }

      if (value === 'false') {
        return !row.original.memberNotes
      }

      return true
    },
    Cell: ({ cell }) => <CellTextWithTooltip cell={cell} />,
    Header: ({ column }) => (
      <HeaderCellWithTooltip column={column} tooltip="Notes that were submitted by Members for this assignment." />
    ),
    Edit: props => {
      const mutation = useAssetAssignmentUpdateMutation({
        onErrorNotification: showErrorNotification,
        onSuccessNotification: showSuccessNotification,
      })

      return (
        <EditTextarea {...props} updateFn={v => mutation.mutateAsync({ id: props.row.original.id, memberNotes: v })} />
      )
    },
  }),
]
